import { createContext, useCallback, useContext, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./toast.css";

const ToastContext = createContext(null);
let idCounter = 0;

// One toast system, replacing the ad hoc per-page <Alert> success/error
// patterns (each page previously managed its own inline success/error
// state with its own placement and timing).
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    clearTimeout(timers.current[id]);
    delete timers.current[id];
  }, []);

  const push = useCallback((variant, message, duration = 4000) => {
    const id = ++idCounter;
    setToasts((list) => [...list, { id, variant, message }]);
    timers.current[id] = setTimeout(() => dismiss(id), duration);
  }, [dismiss]);

  const toast = {
    success: (message, duration) => push("success", message, duration),
    error: (message, duration) => push("error", message, duration),
    info: (message, duration) => push("info", message, duration),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {createPortal(
        <div className="admin-toast-stack" role="region" aria-live="polite" aria-label="Notifications">
          {toasts.map((t) => (
            <div key={t.id} className={`admin-toast admin-toast--${t.variant}`} role="status">
              <span>{t.message}</span>
              <button
                type="button"
                className="admin-toast__close"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
              >
                &times;
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
